import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import { concat, debounce, escapeRegExp, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { CustomButton } from 'src/components/button/Button';
import { SearchBar } from 'src/components/input';
import { NoResultAlert } from 'src/components/noResultAlert';
import { formatProductGridData } from 'src/features/products/listProductsPage/modules/productGrid/ProductGrid.helper';
import {
  useGetInfiniteProductsWithVariations,
  useGetProductVariationFlavors,
  useGetProductVariationTypes,
} from 'src/hooks';
import { IProduct } from 'src/interfaces';
import { lang, normalizeSearchKeywordsBackslashes } from 'src/utils';

import { Modal } from '../Modal';
import { ProductListModalCheckboxes } from './components';
import { TestIds } from './constants';
import { ProductListModalProps } from './ProductListModal.interface';
import {
  ButtonContainer,
  HeaderTitle,
  InfoText,
  ProductListModalContainer,
  ProductListModalHeader,
  ProductListModalResultContainer,
  ProductListModalSubContainer,
  SearchAndCloseIconWrapper,
  StyledSearchBarWrapper,
  ToggleExactMatch,
} from './ProductListModal.styled';

export const ProductListModal = (props: ProductListModalProps) => {
  const [searchProductCodeVal, setSearchProductCodeVal] = useState<string>('');
  const [searchProductNameVal, setSearchProductNameVal] = useState<string>('');
  const [searchProductOverrideNameVal, setSearchProductOverrideNameVal] =
    useState<string>('');
  const [isFuzzySearch, setIsFuzzySearch] = useState(true);
  const [allProducts, setAllProducts] = useState<IProduct[]>([]);
  const [selectedIds, setSelectedIds] = useState<IProduct['product_code'][]>(
    [],
  );

  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Map<IProduct['product_code'], IProduct>
  >(new Map());

  const queryFilters = useMemo(() => {
    const filters = [
      'sort=product_code',
      ...(props.filters || []),
      'pageSize=10',
    ];

    if (!isEmpty(searchProductCodeVal)) {
      filters.push(
        isFuzzySearch
          ? `productVariations.product_code.regexp=${encodeURIComponent(
              escapeRegExp(searchProductCodeVal),
            )}`
          : `productVariations.product_code.list=${encodeURIComponent(
              escapeRegExp(searchProductCodeVal),
            )}`,
      );
    }

    if (!isEmpty(searchProductNameVal)) {
      filters.push(
        `productVariations.name.regexp=${encodeURIComponent(
          normalizeSearchKeywordsBackslashes(searchProductNameVal) as string,
        )}`,
      );
    }

    if (!isEmpty(searchProductOverrideNameVal)) {
      if (isFuzzySearch) {
        filters.push(
          `display_name.filter=${encodeURIComponent(
            normalizeSearchKeywordsBackslashes(
              searchProductOverrideNameVal,
            ) as string,
          )}`,
        );
      } else {
        filters.push(
          `display_name.list=${encodeURIComponent(
            searchProductOverrideNameVal,
          )}`,
        );
      }
    }

    return filters;
  }, [
    searchProductCodeVal,
    searchProductNameVal,
    searchProductOverrideNameVal,
    isFuzzySearch,
    props.filters,
  ]);

  const {
    data: infiniteProductsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetInfiniteProductsWithVariations(queryFilters);

  const { data: productVariationTypes = [] } = useGetProductVariationTypes([
    'sort=*name',
  ]);
  const { data: productVariationFlavors = [] } = useGetProductVariationFlavors([
    'sort=*name',
  ]);

  const loadedCount = allProducts.length;

  const hasResult = loadedCount > 0;

  const totalCount = useMemo(
    () => infiniteProductsData?.pages?.[0]?.totalCount ?? 0,
    [infiniteProductsData],
  );

  const resultLabel = useMemo(
    () => `${lang.result}: ${loadedCount} / ${totalCount}`,
    [loadedCount, totalCount],
  );

  const getNextProductPage = useCallback(
    (inView: boolean) => {
      if (hasNextPage && inView && !isLoading) {
        fetchNextPage();
      }
    },
    [hasNextPage, fetchNextPage, isLoading],
  );

  const selectedProducts = useMemo(() => {
    return selectedIds
      .map((id) => selectedProductsMap.get(id))
      .filter((product): product is IProduct => product !== undefined);
  }, [selectedProductsMap, selectedIds]);

  const handleSelect = () => {
    props.onSelect(selectedProducts);
    props.onClose();
  };

  const handleCancel = () => {
    props.onClose();
  };

  const handleChangeProductCode = debounce((searchVal: string | string[]) => {
    setSearchProductCodeVal(searchVal as string);
  }, 500);

  const handleChangeProductName = debounce((searchVal: string | string[]) => {
    setSearchProductNameVal(searchVal as string);
  }, 500);

  const handleChangeProductOverrideName = debounce(
    (searchVal: string | string[]) => {
      setSearchProductOverrideNameVal(searchVal as string);
    },
    500,
  );

  useEffect(() => {
    const flattenedProducts = formatProductGridData(infiniteProductsData);
    setAllProducts(flattenedProducts);
  }, [infiniteProductsData, isLoading]);

  const onSelectedProduct = (product: IProduct) => {
    setSelectedIds((prevSelectedIds) => {
      const isCurrentlySelected = prevSelectedIds.includes(
        product.product_code,
      );

      if (props.singleSelectionMode) {
        setSelectedProductsMap(() => {
          const newMap = new Map();
          newMap.set(product.product_code, product);

          return newMap;
        });

        return [product.product_code];
      }

      if (isCurrentlySelected) {
        setSelectedProductsMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.delete(product.product_code);

          return newMap;
        });

        return prevSelectedIds.filter((id) => id !== product.product_code);
      } else {
        setSelectedProductsMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(product.product_code, product);

          return newMap;
        });

        return [...prevSelectedIds, product.product_code];
      }
    });
  };

  const onSearchTypeChange = () => setIsFuzzySearch((prev) => !prev);

  return (
    <Modal {...props} maxWidth="lg" fullWidth onClose={handleCancel}>
      <ProductListModalContainer>
        <ProductListModalHeader>
          <HeaderTitle>{props.heading}</HeaderTitle>
          <SearchAndCloseIconWrapper>
            <StyledSearchBarWrapper>
              <Box display="flex" flexDirection="column">
                <SearchBar
                  onInputChange={handleChangeProductCode}
                  placeholder={lang.productCode}
                />

                {props.hasExactSearch && (
                  <Box mt={1}>
                    <ToggleExactMatch>
                      <Typography>{lang.exactMatch}</Typography>
                      <Switch
                        data-testid={TestIds.EXACT_MATCH_TOGGLE}
                        checked={!isFuzzySearch}
                        onChange={onSearchTypeChange}
                      />
                    </ToggleExactMatch>
                    <InfoText>{lang.exactMatchInfo}</InfoText>
                  </Box>
                )}
              </Box>
            </StyledSearchBarWrapper>
            <StyledSearchBarWrapper>
              <SearchBar
                onInputChange={handleChangeProductOverrideName}
                placeholder={lang.productName}
              />
            </StyledSearchBarWrapper>
            <StyledSearchBarWrapper>
              <SearchBar
                onInputChange={handleChangeProductName}
                placeholder={lang.productVariations}
              />
            </StyledSearchBarWrapper>
            <IconButton aria-label="close" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </SearchAndCloseIconWrapper>
        </ProductListModalHeader>

        {!isLoading && totalCount > 0 && (
          <ProductListModalResultContainer>
            <Typography variant="h5">{resultLabel}</Typography>
          </ProductListModalResultContainer>
        )}

        <ProductListModalSubContainer>
          {isLoading ? (
            <CircularProgress
              sx={{ margin: 'auto' }}
              data-testid="product-list-modal-loading-progress"
            />
          ) : !hasResult ? (
            <NoResultAlert />
          ) : (
            <>
              {allProducts.map((item) => (
                <ProductListModalCheckboxes
                  key={item.product_code}
                  product={item}
                  onCheckToggle={onSelectedProduct}
                  checked={selectedIds.includes(item.product_code)}
                  inputValue={concat(
                    searchProductCodeVal,
                    searchProductNameVal,
                    searchProductOverrideNameVal,
                  )}
                  productVariationTypes={productVariationTypes}
                  productVariationFlavors={productVariationFlavors}
                  singleSelectionMode={props.singleSelectionMode}
                />
              ))}

              <InView
                as="div"
                style={{ height: '10px', padding: '10px' }}
                onChange={getNextProductPage}
                disabled={!hasNextPage}
              />
            </>
          )}
        </ProductListModalSubContainer>

        {hasResult && (
          <ButtonContainer>
            <CustomButton variant="text" onClick={handleCancel}>
              {lang.cancel}
            </CustomButton>
            <Button
              variant="contained"
              onClick={handleSelect}
              disabled={isEmpty(selectedIds) || isLoading}>
              {lang.select}
            </Button>
          </ButtonContainer>
        )}
      </ProductListModalContainer>
    </Modal>
  );
};
